/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { withRouter, Link, useHistory, useLocation } from 'react-router-dom';
import { Dropdown } from 'primereact/dropdown';

import { leadsPagesOptions } from 'constants/dropdownOptions';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { getCaseNotificationSummary, resetCaseNotificationSummary } from 'services/notification/notificationService';
import { useWebSocketContext } from 'context/WebSocketConnectionContext';
import useDynamicHeight from 'components/UI/NavbarHeight/useDynamicHeight';
import GlobalTooltip from 'components/GlobalTooltip';
import { ReactComponent as CollapsedIcon } from 'assets/svg/CollapsedIcon.svg';
import { ReactComponent as ExpandedIcon } from 'assets/svg/ExpandedIcon.svg';

function withSidebar(WrappedComponent) {
  function SideBar({ match, ...rest }) {
    const location = useLocation();
    const [menu, setMenu] = useState('');
    const { checkSubPermission, subscriptionAccess } = useUserDetailsContext();
    const websocketContext = useWebSocketContext();

    const history = useHistory();
    const { dynamicHeight } = useDynamicHeight();
    const [isCollapsed, setIsCollapsed] = useState(document.body.classList.contains('sidebar-collapsed'));
    const [notificationSummary, setNotificationSummary] = useState({});

    const notificationTypeMap = {
      files_count: 'files',
      secure_message_count: 'messages',
      text_message_count: 'text-messages',
      emails_count: 'mails',
      forms_count: 'intake',
      calendar_count: 'calendar',
      tasks_count: 'tasks',
      notes_count: 'notes',
    };

    const loadLeadNotificationSummary = () => {
      const leadId = match?.params?.id;
      if (leadId) {
        getCaseNotificationSummary(leadId)
          .then((response) => {
            setNotificationSummary(response?.data || {});
          })
          .catch((error) => {
            console.error('Error loading lead notification summary:', error);
          });
      }
    };

    const handleSidebarClick = (item) => {
      const leadId = match?.params?.id;
      const notificationKey = Object.keys(notificationTypeMap).find((key) => notificationTypeMap[key] === item.value);

      if (notificationKey && notificationSummary[notificationKey] > 0 && leadId) {
        resetCaseNotificationSummary(leadId, notificationKey)
          .then(() => {
            setNotificationSummary((prev) => ({
              ...prev,
              [notificationKey]: 0,
            }));
          })
          .catch((error) => {
            console.error('Error resetting lead notification:', error);
          });
      }
    };

    const handleMenuChange = (e) => {
      setMenu(e.value);
      if (e.value === '') {
        history.push(match.url?.replace(/\/$/, ''));
      }
      history.push(`${match.url?.replace(/\/$/, '')}/${e.value}`);
    };

    const leadOptionFiltered = useMemo(
      () =>
        leadsPagesOptions.filter((o) => {
          if (o.value === 'tasks') {
            return checkSubPermission('task_management');
          } else if (o.value === 'text-messages') {
            return checkSubPermission('texting');
          } else if (o.value === 'mails') {
            return checkSubPermission('email_service');
          } else if (o.value === 'automation') {
            return checkSubPermission('automated_repeatable_workflows');
          } else return true;
        }),
      [subscriptionAccess]
    );

    useEffect(() => {
      let splitted = location?.pathname?.split('/');
      setMenu(splitted[3] ?? '');
      return () => document.body.classList.remove('sidebar-collapsed');
    }, [match]);

    useEffect(() => {
      loadLeadNotificationSummary();
    }, [match?.params?.id]);

    useEffect(() => {
      if (websocketContext?.notificationData) {
        const notification = websocketContext.notificationData;
        const notificationLeadId = notification?.body?.lead_id;
        const currentLeadId = match?.params?.id;

        if (notificationLeadId && currentLeadId && notificationLeadId === currentLeadId) {
          loadLeadNotificationSummary();
        }
      }
    }, [websocketContext?.notificationData]);

    return (
      <div className="container-fluid">
        <div className="row pt-1 justify-content-between">
          <div className="side-nav shadow-middle d-none d-md-block p-0">
            <div
              className="pointer sidebar-toggle-icon"
              onClick={() => {
                document.body.classList.toggle('sidebar-collapsed');
                setIsCollapsed(!isCollapsed);
              }}
            >
              {isCollapsed ? (
                <ExpandedIcon style={{ width: 20, height: 20, color: 'var(--black-400)' }} />
              ) : (
                <CollapsedIcon style={{ width: 20, height: 20, color: 'var(--black-400)' }} />
              )}
            </div>
            <div className="my-3 side-nav-inner">
              {leadOptionFiltered.map((v, i) => {
                const notificationKey = Object.keys(notificationTypeMap).find((key) => notificationTypeMap[key] === v.value);
                const hasNotification = notificationKey && notificationSummary[notificationKey] > 0;

                return (
                  <GlobalTooltip key={i} tooltip={isCollapsed && v.label} showDelay={250} position="right">
                    <Link
                      onClick={() => {
                        setMenu(v.path);
                        handleSidebarClick(v);
                      }}
                      to={v.path ? `${match.url}/${v.path}` : match.url}
                      className={menu === v.path ? 'current-nav' : ''}
                    >
                      <span style={{ position: 'relative', display: 'inline-block' }}>
                        <i className={menu === v.path ? v.icon_active : v.icon} />
                        {isCollapsed && hasNotification && (
                          <span
                            style={{
                              position: 'absolute',
                              top: -3,
                              right: -2,
                              width: 10,
                              height: 10,
                              background: '#1976d2',
                              borderRadius: '50%',
                              display: 'inline-block',
                            }}
                          />
                        )}
                      </span>
                      <span className="menu-label d-flex align-items-center justify-content-between w-100">
                        <span>{v.label}</span>
                        {!isCollapsed && hasNotification && <span className="badge sidebar-new-tag ms-2">NEW</span>}
                      </span>
                    </Link>
                  </GlobalTooltip>
                );
              })}
            </div>
          </div>
          <Dropdown
            className="d-md-none mb-3"
            options={leadOptionFiltered}
            optionValue="path"
            value={menu}
            onChange={(e) => {
              handleMenuChange(e);
              const selectedOption = leadOptionFiltered.find((opt) => opt.path === e.value);
              if (selectedOption) {
                handleSidebarClick(selectedOption);
              }
            }}
            itemTemplate={(option) => {
              const notificationKey = Object.keys(notificationTypeMap).find((key) => notificationTypeMap[key] === option.value);
              const hasNotification = notificationKey && notificationSummary[notificationKey] > 0;
              return (
                <div className="d-flex align-items-center justify-content-between">
                  <span>{option.label}</span>
                  {hasNotification && <span className="badge sidebar-new-tag ms-2">NEW</span>}
                </div>
              );
            }}
            filter
          />
          <div className="case-dm mb-0" style={{ height: `calc(${dynamicHeight} - 18px)` }}>
            <WrappedComponent match={match} {...rest} />
          </div>
        </div>
      </div>
    );
  }

  return withRouter(SideBar);
}

export default withSidebar;
